import React from 'react';
import {
  ArrowForward,
  Link as LinkIcon,
  Mail as MailIcon,
  MeetingRoom as DoorIcon,
  Share as ShareIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

interface FeatureCard {
  icon: React.ReactElement;
  title: string;
  description: string;
}

const FEATURE_CARDS: FeatureCard[] = [
  {
    icon: <LinkIcon />,
    title: 'One permanent link',
    description: 'Yours forever. Share it with patients in JotPsych or anywhere else.',
  },
  {
    icon: <ShareIcon />,
    title: 'Share it however you want',
    description: 'JotPsych appointments, any calendar, email, or text. Your call.',
  },
  {
    icon: <DoorIcon />,
    title: 'Let them in with one click',
    description: "Patients check in using your link. You admit when you're ready.",
  },
];

export interface JotMeetMarketingViewProps {
  /** Pre-formatted monthly price for the addon (e.g. "$29"). */
  priceLabel: string | null;
  /** True for practice admins on group orgs — shapes the "per clinician" copy. */
  isGroupAdmin: boolean;
  /** True for any practice admin (solo or group). Drives admin vs non-admin CTA. */
  isPracticeAdmin: boolean;
  onLearnMore: () => void;
  onNonAdminLearnMore: () => void;
}

/**
 * Marketing pitch shown to users without the JotMeet entitlement.
 *
 * Three feature cards + a role-conditional CTA bar:
 *   - Practice admin → price + "Learn more" (opens the addon onboarding modal)
 *   - Non-admin → "Ask my admin" (opens the same modal's NoAdmin view)
 */
export const JotMeetMarketingView: React.FC<JotMeetMarketingViewProps> = ({
  priceLabel,
  isGroupAdmin,
  isPracticeAdmin,
  onLearnMore,
  onNonAdminLearnMore,
}) => (
  <Stack spacing={2.5}>
    {/* Three feature cards */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 2,
      }}
    >
      {FEATURE_CARDS.map(card => (
        <Box
          key={card.title}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
            bgcolor: 'grey.50',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            {card.icon}
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {card.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {card.description}
          </Typography>
        </Box>
      ))}
    </Box>

    {/* Role-conditional CTA bar */}
    {isPracticeAdmin ? (
      <CtaBar
        icon={<VideoIcon />}
        title={
          priceLabel
            ? `+${priceLabel}/mo${isGroupAdmin ? ' per clinician' : ''}`
            : 'Add JotMeet to your plan'
        }
        subtitle="Or included in the Everything plan. Cancel anytime."
        buttonLabel="Learn more"
        onClick={onLearnMore}
      />
    ) : (
      <CtaBar
        icon={<MailIcon />}
        title="Your practice admin handles subscriptions"
        subtitle="Ask them to add JotMeet to your plan."
        buttonLabel="Learn more"
        onClick={onNonAdminLearnMore}
      />
    )}
  </Stack>
);

const CtaBar: React.FC<{
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  buttonLabel: string;
  onClick: () => void;
}> = ({ icon, title, subtitle, buttonLabel, onClick }) => (
  // Lavender-tinted panel matching the banner + tinted circles used
  // elsewhere in this module (see JotMeetActiveView for the same trick).
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 2,
      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
      borderRadius: 2,
      px: 3,
      py: 2,
    }}
  >
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          fontSize: 24,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
    <Button
      variant="contained"
      endIcon={<ArrowForward />}
      onClick={onClick}
      sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {buttonLabel}
    </Button>
  </Box>
);
